/**
 * Deleted accounts — invoices kept (ADR-079 Amendment 1, 7 October 2026).
 *
 * Jeff, 7 October 2026: deleting a customer removes everything except its invoices, which are tax
 * and audit records — kept for 7 years from the invoice date, then purged by billing's monthly job.
 * This panel is where they stay readable: every zone's deleted accounts with kept invoices, each
 * labelled "Account deleted on <date>", opening to its invoices. READ-ONLY by design — no reverse,
 * no credit: the customer is gone, and an issued invoice is never edited (ADR-083 D3).
 *
 * ⚠ It names the business the invoices were issued to, never a person or an email — the deletion
 * removed those, and this panel must not be the place they reappear.
 */
import { useEffect, useState } from 'react';
import { useToast } from './ToastProvider';
import { InstantTime } from '../lib/time-format';
import { zoneByCode } from '../lib/zones';
import { friendlyFromUnknown } from '../lib/friendly-error';
import {
  getCustomerBilling,
  listDeletedAccountBilling,
  type CustomerBilling,
  type DeletedAccountBilling,
} from '../lib/customers-api';

const money = (n: number) => `$${n.toFixed(2)}`;

type ListState =
  | { status: 'closed' }
  | { status: 'loading' }
  | { status: 'loaded'; items: DeletedAccountBilling[]; failedZones: string[] }
  | { status: 'error' };

export function DeletedAccountInvoices() {
  const toast = useToast();
  // ⭐ LAZY — nothing is fetched until the panel is opened. It is a secondary panel on the
  // Customers page, so it must not add a request (per zone) to that page's data-ready path.
  const [state, setState] = useState<ListState>({ status: 'closed' });
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [billing, setBilling] = useState<Record<string, CustomerBilling | 'loading' | 'error'>>({});

  useEffect(() => {
    if (!expanded) return;
    let live = true;
    setState({ status: 'loading' });
    listDeletedAccountBilling()
      .then((r) => { if (live) setState({ status: 'loaded', items: r.items ?? [], failedZones: r.failedZones ?? [] }); })
      .catch((e) => {
        console.error('deleted-account invoices: list failed', e);
        if (live) setState({ status: 'error' });
      });
    return () => { live = false; };
  }, [expanded]);

  const toggle = async (row: DeletedAccountBilling) => {
    const key = `${row.zone}:${row.accountId}`;
    if (open === key) { setOpen(null); return; }
    setOpen(key);
    if (billing[key] && billing[key] !== 'error') return;
    setBilling((b) => ({ ...b, [key]: 'loading' }));
    try {
      const r = await getCustomerBilling(row.accountId, zoneByCode(row.zone));
      setBilling((b) => ({ ...b, [key]: r }));
    } catch (e) {
      console.error('deleted-account invoices: billing read failed', e);
      setBilling((b) => ({ ...b, [key]: 'error' }));
      toast.push({ variant: 'error', title: 'Could not load these invoices', detail: friendlyFromUnknown(e) });
    }
  };

  return (
    <section className="portal-card portal-deleted-invoices" aria-label="Deleted accounts — invoices kept">
      <header className="portal-card-header">
        <h2>
          <button
            type="button"
            className="portal-link-button portal-deleted-invoices-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '▾' : '▸'} Deleted accounts — invoices kept
          </button>
        </h2>
      </header>
      {expanded && (<>
      <p className="portal-deleted-invoices-intro">
        A deleted customer&apos;s invoices are tax records. They are kept for 7 years from the invoice date
        (longer if this zone&apos;s Nexa Config says so), read-only, then removed by the monthly purge.
      </p>
      {state.status === 'loading' && (
        <p className="portal-deleted-invoices-status" aria-busy="true">Loading deleted accounts…</p>
      )}
      {state.status === 'error' && (
        <p className="portal-deleted-invoices-status" role="alert">
          Could not read the deleted accounts. Retry shortly.
        </p>
      )}
      {state.status === 'loaded' && state.failedZones.length > 0 && (
        <p className="portal-deleted-invoices-status" role="status">
          {state.failedZones.join(' and ')} did not respond — its deleted accounts are missing here.
        </p>
      )}
      {state.status === 'loaded' && state.items.length === 0 && (
        <p className="portal-deleted-invoices-status">No deleted account has invoices kept.</p>
      )}
      {state.status === 'loaded' && state.items.length > 0 && (
        <ul className="portal-deleted-invoices-list">
          {state.items.map((row) => {
            const key = `${row.zone}:${row.accountId}`;
            const b = billing[key];
            return (
              <li key={key}>
                <button
                  type="button"
                  className="portal-link-button portal-deleted-invoices-toggle"
                  aria-expanded={open === key}
                  onClick={() => void toggle(row)}
                >
                  {open === key ? '▾' : '▸'} {row.businessName || row.accountId}
                </button>
                <span className="portal-deleted-invoices-meta">
                  {' '}· {zoneByCode(row.zone)?.label ?? row.zone} · Account deleted on{' '}
                  <InstantTime iso={row.deletedAt} mode="date" /> · {row.invoiceCount} invoice
                  {row.invoiceCount === 1 ? '' : 's'}
                </span>
                {open === key && (
                  b === 'loading' || b === undefined ? (
                    <p className="portal-deleted-invoices-status" aria-busy="true">Loading invoices…</p>
                  ) : b === 'error' ? (
                    <p className="portal-deleted-invoices-status">Could not load these invoices.</p>
                  ) : (
                    <table className="portal-users-table">
                      <thead>
                        <tr>
                          <th scope="col">Invoice</th>
                          <th scope="col">Date</th>
                          <th scope="col">Description</th>
                          <th scope="col">Total</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.invoices.map((inv) => (
                          <tr key={inv.invoiceId}>
                            <td><code>{inv.invoiceId}</code></td>
                            <td><InstantTime iso={inv.createdAt} mode="date" /></td>
                            <td>{inv.description}</td>
                            <td>{money(inv.total)}</td>
                            <td>{inv.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
      </>)}
    </section>
  );
}
